import React, { useEffect, useState } from 'react';
import { Checkbox, Dropdown, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type CommunicationTypeKey = 'outbound_call' | 'inbound_call' | 'email';

const TYPE_OPTIONS: { id: CommunicationTypeKey; labelKey: string }[] = [
  { id: 'outbound_call', labelKey: 'typeOutboundCall' },
  { id: 'inbound_call', labelKey: 'typeInboundCall' },
  { id: 'email', labelKey: 'typeEmail' },
];

interface TypeFilterProps {
  selectedTypes: CommunicationTypeKey[];
  onChange: (selected: CommunicationTypeKey[]) => void;
  mode?: 'dropdown' | 'pill' | 'both';
}

const TypeFilter: React.FC<TypeFilterProps> = ({
  selectedTypes,
  onChange,
  mode = 'both',
}) => {
  const { t } = useTranslation(['aiHistory', 'common']);
  const [mainDropdownOpen, setMainDropdownOpen] = useState(false);
  const [pillDropdownOpen, setPillDropdownOpen] = useState(false);
  const [tempSelected, setTempSelected] =
    useState<CommunicationTypeKey[]>(selectedTypes);

  useEffect(() => {
    if (mainDropdownOpen || pillDropdownOpen) setTempSelected(selectedTypes);
  }, [mainDropdownOpen, pillDropdownOpen, selectedTypes]);

  const close = () => {
    setMainDropdownOpen(false);
    setPillDropdownOpen(false);
  };

  const handleSelect = (id: CommunicationTypeKey, checked: boolean) => {
    setTempSelected((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  };

  const menuContent = (
    <div
      style={{
        backgroundColor: 'var(--surface-elevated)',
        width: 240,
        borderRadius: 12,
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        padding: '12px 16px',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {TYPE_OPTIONS.map((option) => (
        <div key={option.id} style={{ padding: '4px 0' }}>
          <Checkbox
            checked={tempSelected.includes(option.id)}
            onChange={(e) => handleSelect(option.id, e.target.checked)}
            className='custom-menu-shipment-checkbox-only checkbox-sm'
            style={{ fontSize: 12 }}
          >
            {t(option.labelKey)}
          </Checkbox>
        </div>
      ))}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 12,
          gap: 8,
        }}
      >
        <Button
          type='text'
          onClick={() => {
            onChange([]);
            setTempSelected([]);
            close();
          }}
          style={{ fontSize: 12, height: 28, width: 52, fontWeight: 600 }}
        >
          {t('common:reset')}
        </Button>
        <Button
          type='primary'
          onClick={() => {
            onChange(tempSelected);
            close();
          }}
          style={{
            fontWeight: 600,
            height: 28,
            width: 58,
            fontSize: 12,
            borderRadius: 8,
            backgroundColor: 'var(--text-primary)',
            borderColor: 'var(--text-primary)',
            color: 'var(--surface-base)',
          }}
        >
          {t('common:apply')}
        </Button>
      </div>
    </div>
  );

  const firstOption = TYPE_OPTIONS.find((o) => o.id === selectedTypes[0]);
  const showDropdown = mode === 'both' || mode === 'dropdown';
  const showPill = mode === 'both' || mode === 'pill';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {showDropdown && (
        <div className='ai-state-filter'>
          <Dropdown
            dropdownRender={() => menuContent}
            trigger={['click']}
            open={mainDropdownOpen}
            onOpenChange={(flag) => {
              setMainDropdownOpen(flag);
              if (flag) setPillDropdownOpen(false);
            }}
          >
            <button
              className='zl-btn zl-btn--10-600 rounded-full flex-center'
              type='button'
            >
              {t('typeFilter')} <ChevronDownIcon size={10} />
            </button>
          </Dropdown>
        </div>
      )}

      {showPill && selectedTypes.length > 0 && (
        <Dropdown
          dropdownRender={() => menuContent}
          trigger={['click']}
          open={pillDropdownOpen}
          onOpenChange={(flag) => {
            setPillDropdownOpen(flag);
            if (flag) setMainDropdownOpen(false);
          }}
        >
          <button
            className='zl-btn zl-btn-clr rounded-full flex-center'
            style={{ whiteSpace: 'nowrap', gap: 6 }}
            type='button'
          >
            {t('typeFilter')}:{' '}
            {firstOption ? t(firstOption.labelKey) : selectedTypes[0]}
            {selectedTypes.length > 1 ? ` +${selectedTypes.length - 1}` : ''}
            <span
              className='ai-state-close'
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
            >
              <CloseOutlined />
            </span>
          </button>
        </Dropdown>
      )}
    </div>
  );
};

export default TypeFilter;
